/**
 * Study Utilities
 * Helper functions for study-related operations
 */

import { mapDicomModalityToBackend } from './modalityUtils';

export interface SelectedStudy {
  studyInstanceUid: string;
  modalities: string;
  assignedName?: string;
  assignedHubFacilityId?: string;
  sourceFacilityId?: string | number | null;
  studyId?: string | number | null;
  reportStatus?: string;
  /** Display-only fields for assign / reassign radiologist modal */
  patientName?: string;
  studyDescription?: string;
  patientId?: string;
  accessionNumber?: string;
  priority?: string;
  isCritical?: boolean;
}

/**
 * Extract unique modalities from selected studies, mapped to backend format
 */
export function extractUniqueModalities(selectedStudies: SelectedStudy[]): string[] {
  const modalitySet = new Set<string>();

  selectedStudies.forEach(study => {
    if (study.modalities) {
      // Modalities can be comma-separated or space-separated
      const modalities = study.modalities.split(/[,\s]+/).filter(m => m.trim());
      modalities.forEach(modality => {
        const backendModality = mapDicomModalityToBackend(modality.trim());
        if (backendModality) {
          modalitySet.add(backendModality);
        }
      });
    }
  });

  return Array.from(modalitySet);
}

/**
 * Get the first modality from a study's modalities string
 */
export function getFirstModality(modalities: string): string {
  if (!modalities) {
    return '';
  }
  const modalityList = modalities.split(/[,\s]+/).filter(m => m.trim());
  return modalityList[0]?.trim() || '';
}

/**
 * Check if study has approved report status
 */
export function isStudyApproved(study: SelectedStudy): boolean {
  return study.reportStatus !== undefined && study.reportStatus.trim().toLowerCase() === 'approved';
}

/**
 * Check if study is assigned
 */
export function isStudyAssigned(study: SelectedStudy): boolean {
  return study.assignedName !== undefined && study.assignedName.trim() !== '';
}

/**
 * Check if study has study_id in database
 */
export function hasStudyId(study: SelectedStudy): boolean {
  return study.studyId !== null && study.studyId !== undefined && study.studyId !== '';
}
